/**
 * confirmModal — an in-game confirmation dialog rendered inside the game
 * document (never the browser's native confirm()). Used for purchases and any
 * other action that needs a "you sure?" gate so the prompt stays styled and
 * works inside the Picture-in-Picture window, where native confirm() is blocked.
 *
 * @module ui/helpers/confirmModal
 */

/**
 * Show a confirmation overlay and resolve with the user's choice.
 * @param {Document} doc the document to mount into (game or PiP document)
 * @param {object} opts
 * @param {string} opts.title heading text
 * @param {string} [opts.message] body HTML (already-safe strings only)
 * @param {string} [opts.confirmLabel='Confirmar']
 * @param {string} [opts.cancelLabel='Cancelar']
 * @param {string} [opts.icon] optional emoji/glyph shown above the title
 * @returns {Promise<boolean>} true if confirmed, false if cancelled/dismissed
 */
export function confirmModal(doc, opts) {
  const {
    title,
    message = '',
    confirmLabel = 'Confirmar',
    cancelLabel = 'Cancelar',
    icon = '',
  } = opts;

  return new Promise((resolve) => {
    const overlay = doc.createElement('div');
    overlay.style.cssText =
      'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;' +
      'background:rgba(0,0,0,0.55);z-index:10000;animation:fadeIn .15s';

    const card = doc.createElement('div');
    card.style.cssText =
      'background:var(--pd-surface);border:2px solid var(--pd-gold);border-radius:12px;' +
      'padding:18px 22px;max-width:280px;text-align:center;box-shadow:0 8px 28px rgba(0,0,0,0.5)';
    card.innerHTML = `
      ${icon ? `<div style="font-size:1.6rem;margin-bottom:6px">${icon}</div>` : ''}
      <div style="font-size:13px;font-weight:700;color:var(--pd-text);margin-bottom:6px">${title}</div>
      ${message ? `<div style="font-size:11px;color:var(--pd-muted);margin-bottom:14px;line-height:1.4">${message}</div>` : '<div style="margin-bottom:14px"></div>'}
      <div style="display:flex;gap:8px;justify-content:center">
        <button class="pd-btn pd-btn-primary pd-confirm-yes" style="font-size:11px;padding:7px 18px">${confirmLabel}</button>
        <button class="pd-btn pd-btn-secondary pd-confirm-no" style="font-size:11px;padding:7px 18px">${cancelLabel}</button>
      </div>`;

    overlay.appendChild(card);
    doc.body.appendChild(overlay);

    const close = (result) => {
      overlay.remove();
      resolve(result);
    };
    card.querySelector('.pd-confirm-yes').addEventListener('click', () => close(true));
    card.querySelector('.pd-confirm-no').addEventListener('click', () => close(false));
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(false); });
  });
}
